import {
  Checkbox,
  type CheckedState,
} from "components/designV2/checkbox/checkbox";
import type {
  EntityUnitTeamOption,
  UnitTeamOption,
} from "./unitTeamDropdown.logic";
import {
  EntityBlock,
  OptionRow,
  TeamsList,
  UnitBlock,
  UnitsList,
} from "./unitTeamDropdown.styles";

interface UnitOptionProps {
  readonly selectedTeamIds: ReadonlySet<string>;
  readonly selectedUnitId?: number | null;
  readonly showEntityName: boolean;
  readonly unit: UnitTeamOption;
  readonly unknownTeamLabel: string;
  readonly unknownUnitLabel: string;
  readonly onTeamToggle: (teamId: number) => void;
  readonly onUnitToggle: (unit: UnitTeamOption) => void;
}

interface EntityOptionProps {
  readonly entity: EntityUnitTeamOption;
  readonly selectedEntityId?: number | null;
  readonly selectedTeamIds: ReadonlySet<string>;
  readonly selectedUnitId?: number | null;
  readonly unknownTeamLabel: string;
  readonly unknownUnitLabel: string;
  readonly onEntitySelect: (entityId: number) => void;
  readonly onTeamToggle: (teamId: number) => void;
  readonly onUnitToggle: (unit: UnitTeamOption) => void;
}

interface UnitTeamDropdownOptionsProps {
  readonly entities: readonly EntityUnitTeamOption[];
  readonly selectedEntityId?: number | null;
  readonly selectedTeamIds: ReadonlySet<string>;
  readonly selectedUnitId?: number | null;
  readonly showEntityLevel: boolean;
  readonly units: readonly UnitTeamOption[];
  readonly unknownTeamLabel: string;
  readonly unknownUnitLabel: string;
  readonly onEntitySelect: (entityId: number) => void;
  readonly onTeamToggle: (teamId: number) => void;
  readonly onUnitToggle: (unit: UnitTeamOption) => void;
}

interface GetUnitCheckedStateParams {
  readonly selectedTeamIds: ReadonlySet<string>;
  readonly selectedUnitId?: number | null;
  readonly unit: UnitTeamOption;
}

function getUnitCheckedState({
  selectedTeamIds,
  selectedUnitId,
  unit,
}: GetUnitCheckedStateParams): CheckedState {
  if (selectedUnitId === unit.unitId) return "checked";
  const teamIds = unit.teams.map((team) => String(team.id));
  const selectedCount = teamIds.filter((id) => selectedTeamIds.has(id)).length;
  if (teamIds.length > 0 && selectedCount === teamIds.length) return "checked";
  if (selectedCount > 0) return "indeterminate";
  return "unchecked";
}

const UnitOption = (props: UnitOptionProps): JSX.Element => {
  const entityName = (props.unit.entityName ?? "").trim();
  const unitName = (props.unit.unitName ?? "").trim() || props.unknownUnitLabel;
  const unitLabel =
    props.showEntityName && entityName
      ? `${entityName} / ${unitName}`
      : unitName;
  const checkedState = getUnitCheckedState({
    selectedTeamIds: props.selectedTeamIds,
    selectedUnitId: props.selectedUnitId,
    unit: props.unit,
  });

  return (
    <UnitBlock>
      <OptionRow>
        <Checkbox
          label={unitLabel}
          checked={checkedState}
          onChange={() => props.onUnitToggle(props.unit)}
        />
      </OptionRow>
      <TeamsList>
        {props.unit.teams.map((team) => (
          <OptionRow key={team.id}>
            <Checkbox
              label={(team.name ?? "").trim() || props.unknownTeamLabel}
              checked={
                props.selectedTeamIds.has(String(team.id))
                  ? "checked"
                  : "unchecked"
              }
              onChange={() => props.onTeamToggle(team.id)}
            />
          </OptionRow>
        ))}
      </TeamsList>
    </UnitBlock>
  );
};

const EntityOption = (props: EntityOptionProps): JSX.Element => {
  const isSelected = props.selectedEntityId === props.entity.entityId;
  const hasChildSelection =
    props.selectedTeamIds.size > 0 || props.selectedUnitId != null;
  const checkedState: CheckedState = isSelected
    ? hasChildSelection
      ? "indeterminate"
      : "checked"
    : "unchecked";

  return (
    <EntityBlock>
      <OptionRow>
        <Checkbox
          label={props.entity.entityName ?? ""}
          checked={checkedState}
          onChange={() => props.onEntitySelect(props.entity.entityId)}
        />
      </OptionRow>
      <UnitsList>
        {props.entity.units.map((unit) => (
          <UnitOption
            key={unit.unitId}
            selectedTeamIds={props.selectedTeamIds}
            selectedUnitId={props.selectedUnitId}
            showEntityName={false}
            unit={unit}
            unknownTeamLabel={props.unknownTeamLabel}
            unknownUnitLabel={props.unknownUnitLabel}
            onTeamToggle={props.onTeamToggle}
            onUnitToggle={props.onUnitToggle}
          />
        ))}
      </UnitsList>
    </EntityBlock>
  );
};

export const UnitTeamDropdownOptions = (
  props: UnitTeamDropdownOptionsProps,
): JSX.Element => {
  if (props.showEntityLevel) {
    return (
      <>
        {props.entities.map((entity) => (
          <EntityOption
            key={entity.entityId}
            entity={entity}
            selectedEntityId={props.selectedEntityId}
            selectedTeamIds={props.selectedTeamIds}
            selectedUnitId={props.selectedUnitId}
            unknownTeamLabel={props.unknownTeamLabel}
            unknownUnitLabel={props.unknownUnitLabel}
            onEntitySelect={props.onEntitySelect}
            onTeamToggle={props.onTeamToggle}
            onUnitToggle={props.onUnitToggle}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {props.units.map((unit) => (
        <UnitOption
          key={`${unit.entityId ?? "unit"}-${unit.unitId}`}
          selectedTeamIds={props.selectedTeamIds}
          showEntityName
          unit={unit}
          unknownTeamLabel={props.unknownTeamLabel}
          unknownUnitLabel={props.unknownUnitLabel}
          onTeamToggle={props.onTeamToggle}
          onUnitToggle={props.onUnitToggle}
        />
      ))}
    </>
  );
};
